import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { BrandMarkIcon, ChevronRightIcon, MonitorIcon, MoonIcon, SparkIcon, SunIcon } from '@/components/Icons';
import { PasswordInput } from '@/components/PasswordInput';
import { PhoneNumberField } from '@/components/PhoneNumberField';
import { useLaunchState } from '@/context/LaunchContext';
import { useSessionState } from '@/context/SessionContext';
import { useThemeState } from '@/context/ThemeContext';
import {
  getLoginDevicePayload,
  loginWithUsernamePassword,
  requestLoginChallenge,
  verifyLoginOtp,
} from '@/lib/api';
import {
  DEFAULT_PHONE_COUNTRY_CODE,
  joinPhoneNumber,
  normalizePhoneDigits,
  type PhoneCountryCode,
} from '@/lib/phone';
import { loginOtpSchema, loginPhoneSchema, loginUsernamePasswordSchema, flattenFieldErrors, getFieldErrorsFromApiError } from '@/lib/validation';
import type { SessionRole } from '@/lib/types';

type LoginMode = 'phone-otp' | 'username-password';
type ActiveAction = 'request-otp' | 'verify-otp' | 'credentials' | null;

function ThemeCycleButton() {
  const { themeMode, setThemeMode } = useThemeState();

  const nextMode = useMemo(() => {
    switch (themeMode) {
      case 'system':
        return 'light';
      case 'light':
        return 'dark';
      case 'dark':
      default:
        return 'system';
    }
  }, [themeMode]);

  const icon = useMemo(() => {
    switch (themeMode) {
      case 'light':
        return <SunIcon size={18} />;
      case 'dark':
        return <MoonIcon size={18} />;
      case 'system':
      default:
        return <MonitorIcon size={18} />;
    }
  }, [themeMode]);

  return (
    <button
      className="icon-button"
      type="button"
      aria-label="Switch theme mode"
      title={`Switch to ${nextMode} mode`}
      onClick={() => {
        void setThemeMode(nextMode);
      }}>
      {icon}
    </button>
  );
}

export function LoginPage() {
  const navigate = useNavigate();
  const { welcomeSeen, setWelcomeSeen } = useLaunchState();
  const { isAuthenticated, sessionRole, signIn } = useSessionState();
  const [mode, setMode] = useState<LoginMode>('username-password');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneCountryCode, setPhoneCountryCode] = useState<PhoneCountryCode>(DEFAULT_PHONE_COUNTRY_CODE);
  const [otp, setOtp] = useState('');
  const [phoneChallenge, setPhoneChallenge] = useState<{ challengeId: string; maskedPhoneNumber: string; otp?: string } | null>(null);
  const [activeAction, setActiveAction] = useState<ActiveAction>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const isBusy = activeAction !== null;
  const isPhoneMode = mode === 'phone-otp';
  const showDevOtp = import.meta.env.DEV;

  useEffect(() => {
    if (welcomeSeen === false) {
      navigate('/', { replace: true });
    }
  }, [navigate, welcomeSeen]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    navigate(sessionRole === 'staff' ? '/attendance' : '/dashboard', { replace: true });
  }, [isAuthenticated, navigate, sessionRole]);

  useEffect(() => {
    setErrorMessage(null);
    setFieldErrors({});
  }, [mode]);

  function handleModeChange(nextMode: LoginMode) {
    if (isBusy || nextMode === mode) {
      return;
    }

    setMode(nextMode);

    if (nextMode !== 'phone-otp') {
      setPhoneChallenge(null);
      setOtp('');
    }

    setFieldErrors({});
    setErrorMessage(null);
  }

  function handlePhoneNumberChange(value: string) {
    setPhoneNumber(normalizePhoneDigits(value));
    setFieldErrors((current) => {
      if (!current.phoneNumber && !current.otp) {
        return current;
      }

      const next = { ...current };
      delete next.phoneNumber;
      delete next.otp;
      return next;
    });
    setErrorMessage(null);

    if (phoneChallenge) {
      setPhoneChallenge(null);
      setOtp('');
    }
  }

  function handlePhoneCountryCodeChange(nextCountryCode: PhoneCountryCode) {
    setPhoneCountryCode(nextCountryCode);
    setFieldErrors((current) => {
      if (!current.phoneNumber && !current.otp) {
        return current;
      }

      const next = { ...current };
      delete next.phoneNumber;
      delete next.otp;
      return next;
    });
    setErrorMessage(null);

    if (phoneChallenge) {
      setPhoneChallenge(null);
      setOtp('');
    }
  }

  async function handleRequestOtp() {
    if (isBusy) {
      return;
    }

    const validation = loginPhoneSchema.safeParse({ phoneNumber });

    if (!validation.success) {
      setFieldErrors(flattenFieldErrors(validation.error.flatten().fieldErrors));
      setErrorMessage(null);
      return;
    }

    setActiveAction('request-otp');
    setFieldErrors({});
    setErrorMessage(null);

    try {
      const device = getLoginDevicePayload();
      const response = await requestLoginChallenge({
        type: 'otp',
        phoneNumber: joinPhoneNumber(phoneCountryCode, validation.data.phoneNumber),
        device,
      });

      setPhoneChallenge({
        challengeId: response.challengeId,
        maskedPhoneNumber: response.maskedPhoneNumber,
        otp: response.otp,
      });

      if (response.otp) {
        setOtp(response.otp);
      }
    } catch (error) {
      const apiFieldErrors = getFieldErrorsFromApiError(error);

      if (Object.keys(apiFieldErrors).length > 0) {
        setFieldErrors((current) => ({ ...current, ...apiFieldErrors }));
        setErrorMessage(null);
        return;
      }

      if (error instanceof Error && /no account found for this phone number/i.test(error.message)) {
        setFieldErrors({ phoneNumber: error.message });
        setErrorMessage(null);
        return;
      }

      setErrorMessage(error instanceof Error && error.message ? error.message : 'Something went wrong. Please try again.');
    } finally {
      setActiveAction(null);
    }
  }

  async function handleVerifyOtp() {
    if (isBusy || !phoneChallenge) {
      return;
    }

    const validation = loginOtpSchema.safeParse({ otp });

    if (!validation.success) {
      setFieldErrors(flattenFieldErrors(validation.error.flatten().fieldErrors));
      setErrorMessage(null);
      return;
    }

    setActiveAction('verify-otp');
    setFieldErrors((current) => {
      if (!current.otp) {
        return current;
      }

      const next = { ...current };
      delete next.otp;
      return next;
    });
    setErrorMessage(null);

    try {
      const response = await verifyLoginOtp({
        challengeId: phoneChallenge.challengeId,
        otp: validation.data.otp,
        device: getLoginDevicePayload(),
      });

      const nextRole: SessionRole = response.user.role === 'staff' ? 'staff' : 'enterprise';

      await signIn({
        token: response.token,
        role: nextRole,
        name: response.user.name,
        username: response.user.username,
        staffId: nextRole === 'staff' ? response.user.id : null,
      });

      navigate(nextRole === 'staff' ? '/attendance' : '/dashboard', { replace: true });
    } catch (error) {
      const apiFieldErrors = getFieldErrorsFromApiError(error);

      if (Object.keys(apiFieldErrors).length > 0) {
        setFieldErrors((current) => ({ ...current, ...apiFieldErrors }));
        setErrorMessage(null);
        return;
      }

      if (error instanceof Error) {
        const message = error.message.toLowerCase();

        if (message.includes('no account found for this phone number')) {
          setFieldErrors({ phoneNumber: error.message });
          setErrorMessage(null);
          return;
        }

        if (
          message.includes('invalid otp') ||
          message.includes('otp challenge is invalid or expired') ||
          message.includes('otp attempts exhausted')
        ) {
          setFieldErrors({ otp: error.message });
          setErrorMessage(null);
          return;
        }
      }

      setErrorMessage(error instanceof Error && error.message ? error.message : 'Something went wrong. Please try again.');
    } finally {
      setActiveAction(null);
    }
  }

  async function handleCredentialLogin() {
    if (isBusy) {
      return;
    }

    const validation = loginUsernamePasswordSchema.safeParse({ username, password });

    if (!validation.success) {
      setFieldErrors(flattenFieldErrors(validation.error.flatten().fieldErrors));
      setErrorMessage(null);
      return;
    }

    setActiveAction('credentials');
    setFieldErrors({});
    setErrorMessage(null);

    try {
      const response = await loginWithUsernamePassword({
        type: 'username',
        username: validation.data.username,
        password: validation.data.password,
        device: getLoginDevicePayload(),
      });

      const nextRole: SessionRole = response.user.role === 'staff' ? 'staff' : 'enterprise';

      await signIn({
        token: response.token,
        role: nextRole,
        name: response.user.name,
        username: response.user.username,
        staffId: nextRole === 'staff' ? response.user.id : null,
      });

      navigate(nextRole === 'staff' ? '/attendance' : '/dashboard', { replace: true });
    } catch (error) {
      const apiFieldErrors = getFieldErrorsFromApiError(error);

      if (Object.keys(apiFieldErrors).length > 0) {
        setFieldErrors((current) => ({ ...current, ...apiFieldErrors }));
        setErrorMessage(null);
        return;
      }

      if (error instanceof Error && /invalid username or password/i.test(error.message)) {
        setFieldErrors({
          username: error.message,
          password: error.message,
        });
        setErrorMessage(null);
        return;
      }

      setErrorMessage(error instanceof Error && error.message ? error.message : 'Something went wrong. Please try again.');
    } finally {
      setActiveAction(null);
    }
  }

  return (
    <div className="app-root">
      <div className="ambient-glow one" />
      <div className="ambient-glow two" />
      <div className="ambient-glow three" />

      <div className="page-shell">
        <div className="page-shell-inner">
          <div className="page-surface">
            <header className="top-nav login-top-nav">
              <div className="brand-lockup">
                <div className="brand-mark" aria-hidden="true">
                  <BrandMarkIcon />
                </div>
                <div className="brand-copy">
                  <div className="brand-name">SlotLooks</div>
                  <div className="brand-subtitle">Secure access</div>
                </div>
              </div>

              <div className="header-actions">
                <ThemeCycleButton />
              </div>
            </header>

            <main className="login-layout">
              <section className="hero-copy login-copy">
                <span className="eyebrow">
                  <SparkIcon size={14} />
                  <strong>Secure access</strong>
                  <span>Admin and staff</span>
                </span>

                <h1 className="display-title">
                  <span>Welcome</span>
                  <span>back</span>
                </h1>

                <p className="copy">
                  Use the same web flow as the mobile app with OTP or username login.
                </p>

                <div className="chip-row form-stack">
                  <span className="chip active">Role-based access</span>
                  <span className="chip">OTP challenge</span>
                  <span className="chip">Light / dark / system</span>
                </div>
              </section>

              <section className="panel login-card">
                <div className="panel-title-row login-card-title-row">
                  <div>
                    <h1 className="panel-title">Sign in</h1>
                    <p className="panel-caption">Use your account to manage bookings, attendance, and clients.</p>
                  </div>
                  <div className="badge soft">
                    <BrandMarkIcon width={16} height={16} />
                    {isPhoneMode ? 'OTP' : 'Password'}
                  </div>
                </div>

                <div className="chip-row login-mode-row">
                  <button
                    className={`chip${mode === 'username-password' ? ' active' : ''}`}
                    type="button"
                    onClick={() => handleModeChange('username-password')}>
                    Username
                  </button>
                  <button
                    className={`chip${mode === 'phone-otp' ? ' active' : ''}`}
                    type="button"
                    onClick={() => handleModeChange('phone-otp')}>
                    Phone Number
                  </button>
                </div>

                {errorMessage ? <div className="notice error bottom-stack">{errorMessage}</div> : null}

                {isPhoneMode ? (
                  <div className="field-grid">
                    <PhoneNumberField
                      countryCode={phoneCountryCode}
                      phoneNumber={phoneNumber}
                      onChangeCountryCode={handlePhoneCountryCodeChange}
                      onChangePhoneNumber={handlePhoneNumberChange}
                      errorText={fieldErrors.phoneNumber}
                      disabled={isBusy}
                    />

                    {phoneChallenge ? (
                      <div className="notice stacked-note">
                        <strong>Challenge created</strong>
                        <span>OTP sent to {phoneChallenge.maskedPhoneNumber}. Enter the 6 digit code below.</span>
                        {phoneChallenge.otp && showDevOtp ? (
                          <span className="badge">Dev OTP: {phoneChallenge.otp}</span>
                        ) : null}
                      </div>
                    ) : null}

                    <label className="field">
                      <span className="field-label">OTP</span>
                      <input
                        className="input"
                        value={otp}
                        onChange={(event) => setOtp(event.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="123456"
                        inputMode="numeric"
                        maxLength={6}
                      />
                      {fieldErrors.otp ? <span className="field-error">{fieldErrors.otp}</span> : null}
                    </label>

                    <div className="button-row">
                      {!phoneChallenge ? (
                        <button className="primary-button" type="button" onClick={handleRequestOtp} disabled={isBusy}>
                          {activeAction === 'request-otp' ? 'Requesting...' : 'Request OTP'}
                          <ChevronRightIcon size={16} />
                        </button>
                      ) : (
                        <button className="primary-button" type="button" onClick={handleVerifyOtp} disabled={isBusy}>
                          {activeAction === 'verify-otp' ? 'Verifying...' : 'Verify OTP'}
                          <ChevronRightIcon size={16} />
                        </button>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="field-grid">
                    <label className="field">
                      <span className="field-label">Username</span>
                      <input
                        className="input"
                        value={username}
                        onChange={(event) => setUsername(event.target.value)}
                        placeholder="admin"
                        autoComplete="username"
                      />
                      {fieldErrors.username ? <span className="field-error">{fieldErrors.username}</span> : null}
                    </label>

                    <label className="field">
                      <span className="field-label">Password</span>
                      <PasswordInput
                        className="input"
                        value={password}
                        onChange={(event) => setPassword(event.target.value)}
                        placeholder="••••••••"
                        autoComplete="current-password"
                      />
                      {fieldErrors.password ? <span className="field-error">{fieldErrors.password}</span> : null}
                    </label>

                    <div className="button-row">
                      <button className="primary-button" type="button" onClick={handleCredentialLogin} disabled={isBusy}>
                        {activeAction === 'credentials' ? 'Signing in...' : 'Sign in'}
                        <ChevronRightIcon size={16} />
                      </button>
                    </div>
                  </div>
                )}
              </section>
            </main>
          </div>
        </div>
      </div>
    </div>
  );
}
